import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { MultiTableAnalysisWidgetData } from '../types/gotypes'
import {
  BulkRunItem,
  BulkRunPollOutcome,
  BulkRunTarget,
  BulkRunStartResult,
  applyPollOutcomes,
  applyStartResults,
  bulkRunCounts,
  hasActiveBulkRunItems,
  isTerminalBulkRunState,
  makeBulkRunItems,
  markBulkRunGaveUp,
  mergeBulkRunItems,
  resetItemsForRetry,
} from '../utils/dashboardBulkRun'

const POLL_INTERVAL_MS = 3000
/** ~20 minutes at 3s — a bounded give-up so a stuck analysis stops polling. */
const MAX_POLL_TICKS = 400
const CACHE_POLL_INTERVAL_MS = 2000
/** Warming is a courtesy, not a gate: proceed with the run after this. */
const MAX_CACHE_WAIT_MS = 90_000

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export interface BulkRunOutcome {
  items: BulkRunItem[]
  /** Widgets whose start request actually queued a NEW analysis. A 409 (an
   *  analysis already in flight) queues nothing and is excluded. */
  freshWidgets: MultiTableAnalysisWidgetData[]
  startedCount: number
  failedCount: number
  /** #1219 gate 2: refused for lacking overwrite authority — reported apart
   *  from `failedCount` so the summary reads "n not permitted", not "n
   *  failed", and "Retry failed" is never offered for these. */
  refusedCount: number
}

interface UseDashboardBulkRunProps {
  customer: string
  /** Fallback job for widgets that don't carry their own. */
  job: string
  dashboardId: string
}

/**
 * Owns the "Regenerate AI Insights" run: warms the table cache, starts one
 * analysis per widget, and polls each analysis to a terminal state.
 *
 * All the networking for the run lives here so the page only renders state
 * (Page -> Hook -> Component); the state transitions themselves are pure
 * functions in `utils/dashboardBulkRun` and are unit tested.
 */
export function useDashboardBulkRun({ customer, job, dashboardId }: UseDashboardBulkRunProps) {
  const queryClient = useQueryClient()
  const [run, setRun] = useState<{ label: string; items: BulkRunItem[] } | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [isWarmingCache, setIsWarmingCache] = useState(false)
  const [cacheProgress, setCacheProgress] = useState<{ total: number; cached: number } | null>(null)

  const itemsRef = useRef<BulkRunItem[]>([])
  const labelRef = useRef('')
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollBusyRef = useRef(false)
  const pollTicksRef = useRef(0)
  const unmountedRef = useRef(false)
  // Mirrors isStarting synchronously. Two clicks (or a click and a voice tool)
  // in the same tick both see the old state value, so the double-run gate has
  // to read a ref.
  const isStartingRef = useRef(false)

  const markStarting = useCallback((starting: boolean) => {
    isStartingRef.current = starting
    if (!unmountedRef.current) setIsStarting(starting)
  }, [])

  const commit = useCallback((items: BulkRunItem[], label?: string) => {
    // Nothing consumes state after unmount, and setRun would be a stray update
    // from an async tail (a slow start request, a poll already in flight).
    if (unmountedRef.current) return
    itemsRef.current = items
    if (label !== undefined) labelRef.current = label
    setRun({ label: labelRef.current, items })
  }, [])

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      unmountedRef.current = true
      stopPolling()
    }
  }, [stopPolling])

  // The bulk run talks to the analysis endpoints over raw axios, but the
  // widgets render from react-query caches whose polling only wakes while THEY
  // see a running analysis. Without this nudge a widget never learns that a
  // regeneration started or finished, and the fresh text only appears after a
  // browser refresh (#1133). Invalidated by key prefix so every analysis
  // widget's list query refetches, sees the new pending/running analysis, and
  // takes over polling to completion itself.
  const invalidateAnalysisQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['multi_table_analyses'] })
  }, [queryClient])

  const pollOnce = useCallback(async () => {
    // An unmounted tick must CLEAR the interval, not just skip its body — a
    // return here would leave the timer running for the life of the tab.
    if (unmountedRef.current) {
      stopPolling()
      return
    }
    // In-flight guard: a slow round of progress requests must not overlap the
    // next tick, or a stale snapshot can overwrite newer state.
    if (pollBusyRef.current) return
    pollBusyRef.current = true
    try {
      pollTicksRef.current += 1
      const pending = itemsRef.current.filter(
        item => item.analysisId && !isTerminalBulkRunState(item.state)
      )
      if (pending.length === 0) {
        stopPolling()
        return
      }
      const outcomes: BulkRunPollOutcome[] = await Promise.all(
        pending.map(async item => {
          const jobForWidget = item.widget.job || job
          try {
            const res = await axios.get(
              `${API_BASE_URL}/multitables/${customer}/${jobForWidget}/${item.widget.multi_table_id}/analysis/${item.analysisId}/progress`
            )
            return {
              key: item.key,
              analysisId: item.analysisId as string,
              ok: true,
              state: res.data?.state,
              error: res.data?.error,
            }
          } catch {
            return { key: item.key, analysisId: item.analysisId as string, ok: false }
          }
        })
      )
      // Unmounted while the progress requests were in flight — same rule.
      if (unmountedRef.current) {
        stopPolling()
        return
      }
      // Re-read the ref: a retry may have merged new items in while we waited.
      const before = itemsRef.current
      let next = applyPollOutcomes(before, outcomes)
      if (pollTicksRef.current >= MAX_POLL_TICKS) {
        next = markBulkRunGaveUp(next)
      }
      commit(next)
      // A card finished (or failed) this tick — refetch the widgets' analyses
      // so the completed report replaces the stale text in place (#1133). The
      // start-time invalidation already woke each widget's own polling, so this
      // is belt-and-braces for a widget that mounted or refocused mid-run.
      const terminalBefore = before.filter(item => isTerminalBulkRunState(item.state)).length
      const terminalAfter = next.filter(item => isTerminalBulkRunState(item.state)).length
      if (terminalAfter > terminalBefore) invalidateAnalysisQueries()
      if (!hasActiveBulkRunItems(next)) stopPolling()
    } finally {
      pollBusyRef.current = false
    }
  }, [commit, customer, invalidateAnalysisQueries, job, stopPolling])

  const startPolling = useCallback(() => {
    stopPolling()
    pollTicksRef.current = 0
    pollTimerRef.current = setInterval(() => { void pollOnce() }, POLL_INTERVAL_MS)
  }, [pollOnce, stopPolling])

  /**
   * Pre-fetch every table the dashboard's analysis widgets need, and wait for
   * it. Sequencing matters: cache-rebuild jobs and analysis jobs share a queue,
   * so firing both at once makes them compete (#972 asks for auto-warm
   * *before* a run). Best-effort — a failure or a slow warm just proceeds.
   */
  const warmCacheAndWait = useCallback(async () => {
    if (!dashboardId || dashboardId === 'new') return
    setIsWarmingCache(true)
    setCacheProgress(null)
    try {
      const res = await axios.post(`${API_BASE_URL}/dashboards/${customer}/${dashboardId}/warm-cache`)
      const enqueued = res.data?.tablesEnqueued || 0
      if (enqueued <= 0) return
      const deadline = Date.now() + MAX_CACHE_WAIT_MS
      while (Date.now() < deadline && !unmountedRef.current) {
        await sleep(CACHE_POLL_INTERVAL_MS)
        try {
          const progress = await axios.get(
            `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/warm-cache/progress`
          )
          const { total, cached } = progress.data || {}
          setCacheProgress({ total, cached })
          if (total > 0 && cached >= total) return
        } catch {
          // keep waiting until the deadline
        }
      }
    } catch {
      // A cold cache only makes the run slower; it does not stop it.
    } finally {
      if (!unmountedRef.current) {
        setIsWarmingCache(false)
        setCacheProgress(null)
      }
    }
  }, [customer, dashboardId])

  const submit = useCallback(
    async (targetItems: BulkRunItem[], label: string, mode: 'replace' | 'merge'): Promise<BulkRunOutcome | null> => {
      commit(mode === 'replace' ? targetItems : mergeBulkRunItems(itemsRef.current, targetItems), label)
      markStarting(true)
      try {
        await warmCacheAndWait()
        // The cache warm can hold this for ~90s and the user can leave the
        // editor meanwhile. Starting a poll after the hook's cleanup has run
        // creates an interval nothing will ever clear.
        if (unmountedRef.current) return null
        const results = (await Promise.allSettled(
          targetItems.map(item =>
            axios.post(
              `${API_BASE_URL}/multitables/${customer}/${item.widget.job || job}/${item.widget.multi_table_id}/analysis/start`,
              {
                persona: item.widget.persona || 'Executive Summarizer',
                user_context: item.widget.user_context || '',
                synthesis_user_context: item.widget.synthesis_user_context || '',
                output_length: item.widget.output_length || 'standard',
                force: true,
                analysis_instructions: item.widget.analysis_instructions || '',
                synthesis_instructions: item.widget.synthesis_instructions || '',
                // #1810: the card's page filters; the server keeps only the
                // jobs the card's tables use.
                job_filters: item.jobFilters,
              }
            )
          )
        )) as unknown as BulkRunStartResult[]

        if (unmountedRef.current) return null
        const { items: settled, freshKeys } = applyStartResults(targetItems, results)
        const merged = mergeBulkRunItems(itemsRef.current, settled)
        commit(merged, label)
        // Wake the widgets: their analyses list queries refetch, see the new
        // pending/running analyses, and poll them to completion (#1133).
        invalidateAnalysisQueries()
        if (hasActiveBulkRunItems(merged)) startPolling()

        const fresh = new Set(freshKeys)
        return {
          items: merged,
          freshWidgets: settled.filter(item => fresh.has(item.key)).map(item => item.widget),
          startedCount: settled.filter(item => item.state !== 'failed' && item.state !== 'refused').length,
          failedCount: settled.filter(item => item.state === 'failed').length,
          refusedCount: settled.filter(item => item.state === 'refused').length,
        }
      } finally {
        markStarting(false)
      }
    },
    [commit, customer, invalidateAnalysisQueries, job, markStarting, startPolling, warmCacheAndWait]
  )

  const startRun = useCallback(
    async (targets: BulkRunTarget[], label: string): Promise<BulkRunOutcome | null> => {
      if (targets.length === 0) return null
      // The gate lives here, not only on the button: a voice tool, a stale
      // closure, or a second click all reach this function, and a second run
      // 409s against the first and reports the OLD analysis as started.
      if (isStartingRef.current || hasActiveBulkRunItems(itemsRef.current)) return null
      stopPolling()
      return submit(makeBulkRunItems(targets), label, 'replace')
    },
    [stopPolling, submit]
  )

  const retryFailed = useCallback(async (): Promise<BulkRunOutcome | null> => {
    const failed = itemsRef.current.filter(item => item.state === 'failed')
    if (failed.length === 0) return null
    return submit(resetItemsForRetry(failed), labelRef.current, 'merge')
  }, [submit])

  const dismiss = useCallback(() => {
    stopPolling()
    itemsRef.current = []
    setRun(null)
  }, [stopPolling])

  const items = run?.items || []
  const counts = bulkRunCounts(items)
  const isActive = hasActiveBulkRunItems(items)

  return {
    run,
    counts,
    /** True while any card is still queued/running — the regenerate control
     *  must stay disabled until this clears, otherwise a second run 409s
     *  against the first and reports the OLD analysis as started. */
    isBusy: isStarting || isActive,
    isStarting,
    isWarmingCache,
    cacheProgress,
    startRun,
    retryFailed,
    dismiss,
  }
}

export default useDashboardBulkRun
